<template>
  <div>
    <!-- button -->
    <div class="text-nowrap">
      <slot name="additional-button" />
      <b-btn
        id="btn-with-tooltip"
        variant="v3_teal_rounded"
        :class="buttonClass"
        size="sm"
        v-b-modal="refId"
        :disabled="isDisabled"
        @mouseover="$refs.btnTooltip.$emit('open')"
        @mouseout="$refs.btnTooltip.$emit('close')"
      >
        {{ label }}
      </b-btn>
      <b-tooltip ref="btnTooltip" target="btn-with-tooltip">
        {{ tooltipText }}
      </b-tooltip>
    </div>
    <b-modal
      :id="refId"
      :ref="refId"
      size="md"
      lazy
      hide-footer
      @show="onModalShow"
      dialog-class="survey-results--modal-dialog"
      modal-class="right"
      header-class="py-1 pr-1 pl-3"
      content-class="rounded_0 text-left"
    >
      <!-- header / toggle showAll -->

      <template slot="modal-header">
        <span
          class="align-self-center my-0 v2_color_black v2_font_weight_semibold v2_font_size_24"
        >
          {{ label }}
        </span>
        <div>
          <b-button
            variant="v2_primary"
            class=""
            size="sm"
            @click="close"
            v-b-tooltip.hover
            :title="applyTooltipText"
          >
            Done
          </b-button>
        </div>
      </template>

      <!-- filter -->
      <b-form>
        <ChartsCheckboxes
          :key="modalOpensCounter"
          :initial-derived-variables="visibleDerivedVariables"
          :initial-sections="this.sectionsData"
          :initial-membership-type="membershipTypeOption"
          :with-variables="withVariables"
          :is-profile-section-visible="isProfileSectionVisible"
          :is-membership-section-visible="isMembershipSectionVisible"
          :is-on-dashboard-only-selectable="isOnDashboardOnlySelectable"
          :completed-modules="completedModules"
          ref="chartCheckboxes"
        />
      </b-form>
    </b-modal>
  </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue';
import Questionnaire from '@/providers/Questionnaire.js';
import ChartsCheckboxes from '@/components/survey/results/ChartsCheckboxes';
import OpensCounterMixin from '@/mixins/OpensCounterMixin';

export default Vue.extend({
  name: 'ChartsPicker',
  mixins: [OpensCounterMixin],
  components: {
    ChartsCheckboxes,
  },
  props: {
    refId: String,
    questionnaire: Object,
    ids: Object, // format: { derivedVarNames: [], questionIds: [], variableIds: [], membershipIds: [] }
    favorites: {
      default: null,
      validator: (prop) => typeof prop === 'object' || prop === null,
    },
    withVariables: {
      type: Boolean,
      default: false,
    },
    buttonClass: {
      type: String,
    },
    itemsName: {
      type: String,
      default: 'Filters',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
    },
    applyTooltipText: {
      type: String,
    },
    isMembershipSectionVisible: {
      type: Boolean,
      default: false,
    },
    isOnDashboardOnlySelectable: {
      type: Boolean,
      default: false,
    },
    selectedSection: {
      validator: (prop) => typeof prop === 'number' || prop === null,
      default: null,
    },
    completedModules: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data: function () {
    return {
      selectedValues: { ...(this.ids || {}) },
      sectionsData: [], 
    };
  },
  computed: {
    derivedVariables: function () {
      const selectedNames = (this.selectedValues || {}).derivedVarNames || [];
      return Questionnaire.getDerivedVariables(selectedNames);
    },
    sections: function () {
      // const selectedQsIds = (this.selectedValues || {}).questionIds || [];
      // const selectedVIds = (this.selectedValues || {}).variableIds || [];
      // let sections = this.questionnaire.sections;
      // if (this.selectedSection) {
      //   const section = this.questionnaire.sections.find(
      //     (s) => s.id === this.selectedSection
      //   );
      //   sections = [section];
      // }
      // return Questionnaire.getQuestionnaireSections(
      //   sections,
      //   selectedQsIds,
      //   selectedVIds
      // );      
      return this.sectionsData;
    },
    visibleDerivedVariables: function () {
      return this.derivedVariables;
    },
    visibleSections: function () {
      return this.sections;
    },
    label: function () {
      return `Select ${this.itemsName}`;
    },
    isProfileSectionVisible: function () {
      return (
        this.itemsName === 'Filters' ||
        !!this.questionnaire.supports_profile_measures
      );
    },
    membershipTypeOption: function () {
      return {
        label: 'Membership Type',
        selected: !!(this.selectedValues || {}).membershipType,
      };
    },
  },
  methods: {
    close: function () {
      this.$emit('selected', this.$refs.chartCheckboxes.getSelected());
      
      this.sectionsData = Questionnaire.getQuestionnaireSections(
            this.questionnaire.sections,
            (this.$refs.chartCheckboxes.getSelected()|| {}).questionIds|| [],
            (this.$refs.chartCheckboxes.getSelected() || {}).variableIds || []
          );

      this.$refs[this.refId].hide();
    },
    cancel: function () {
      this.$refs[this.refId].hide(true);
    },
    clearAll: function () {
      this.$refs.chartCheckboxes.clearAll();
    },
    selectAll: function () {
      this.$refs.chartCheckboxes.selectAll();
    },
  },
  watch: {
    questionnaire: {
      handler: function (newVal) {
        if (newVal && newVal.sections && newVal.sections.length > 0) {
          const selectedQsIds = (this.selectedValues || {}).questionIds || [];
          const selectedVIds = (this.selectedValues || {}).variableIds || [];
          let sections = newVal.sections;
        
          if (this.selectedSection) {
            const section = newVal.sections.find(
              (s) => s.id === this.selectedSection
            );
            sections = [section];
          }
          
          this.sectionsData = Questionnaire.getQuestionnaireSections(
            sections,
            selectedQsIds,
            selectedVIds
          );
        }
      },
      immediate: true
    },
    ids: function (newIds) {
      this.selectedValues = { ...(newIds || {}) };
    },
    'ids.membershipType': function (newVal) {
      this.selectedValues.membershipType = newVal;
    },
    'ids.derivedVarNames': function (newVal) {
      this.selectedValues.derivedVarNames = newVal;
    },
    'ids.questionIds': function (newVal) {

      this.sectionsData = Questionnaire.getQuestionnaireSections(
      this.questionnaire.sections,newVal,this.selectedValues.derivedVarNames);
      this.selectedValues.questionIds = newVal;
    },
    'ids.variableIds': function (newVal) {
      this.selectedValues.variableIds = newVal;
    },
  },
});
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.variable-picker {
  display: inline-block;
  .modal-body {
    padding: 0 0 10px 0;
    .survey-results__variable-picker__filters-header {
      padding: 20px;
    }
  }
}
</style>
